import type {
  DatabaseAllEvents,
  DatabaseAllViewEvents,
  EventTraceFn,
} from '@blocksuite/affine-shared/services';
import type { UniComponent } from '@blocksuite/affine-shared/types';
import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import type { DisposableMember } from '@blocksuite/global/disposable';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  type EventName,
  ShadowlessElement,
  type UIEventHandler,
} from '@blocksuite/std';
import { measureGfxPerformance } from '@blocksuite/std/gfx';
import { computed } from '@preact/signals-core';
import { property } from 'lit/decorators.js';

import type { DataViewRootUILogic } from '../data-view.js';
import type { DataViewSelection } from '../types.js';
import type { SingleView } from '../view-manager/single-view.js';
import type { DataViewWidget } from '../widget/index.js';
import {
  checkDataViewBodyContract,
  collectDataViewBodyResizeTargets,
  DATA_VIEW_BODY_CONTENT_ATTRIBUTE,
  DATA_VIEW_BODY_INTRINSIC_ATTRIBUTE,
  DATA_VIEW_BODY_RENDER_CONTENT_ATTRIBUTE,
  DATA_VIEW_BODY_RENDER_ROOT_ATTRIBUTE,
  DATA_VIEW_BODY_STATE_ATTRIBUTE,
  getDataViewBodyElement,
} from './data-view-body.js';
import type { DataViewInstance, DataViewProps } from './types.js';

const DATA_VIEW_BODY_CONTRACT_STABLE_FRAME_THRESHOLD = 2;

export const DATABASE_LIVE_VIEW_INTRINSIC_HEIGHT_CHANGE_EVENT =
  'database-live-view-intrinsic-height-change';

export const DATABASE_LIVE_VIEW_SETUP_STATE_CHANGE_EVENT =
  'database-live-view-setup-state-change';

export const DATABASE_LIVE_VIEW_SELECTOR = [
  'dv-table-view-ui-virtual',
  'dv-kanban-view-ui',
  'dv-gantt-view-ui',
  'dv-calendar-view-ui',
].join(',');

export function dispatchDatabaseLiveViewSetupStateChange(
  target: EventTarget,
  open: boolean
): void {
  target.dispatchEvent(
    new CustomEvent(DATABASE_LIVE_VIEW_SETUP_STATE_CHANGE_EVENT, {
      bubbles: true,
      composed: true,
      detail: { open },
    })
  );
}

export abstract class DataViewBase<
  Selection extends DataViewSelection = DataViewSelection,
> extends SignalWatcher(WithDisposable(ShadowlessElement)) {
  abstract expose: DataViewInstance;

  @property({ attribute: false })
  accessor props!: DataViewProps<Selection>;
}
export abstract class DataViewUIBase<
  Logic extends DataViewUILogicBase = DataViewUILogicBase,
> extends SignalWatcher(WithDisposable(ShadowlessElement)) {
  @property({ attribute: false })
  accessor logic!: Logic;

  private dataViewBodyResizeObserver: ResizeObserver | undefined;
  private dataViewBodyResizeTargets: HTMLElement[] = [];
  private dataViewBodyContractFrame: number | undefined;
  private dataViewBodyContractViolationCandidateCount = 0;
  private dataViewBodyContractViolationCandidateSignature: string | undefined;
  private dataViewBodyContractViolationSignature: string | undefined;
  private dataViewIntrinsicHeightChangeFrame:
    | ReturnType<typeof requestAnimationFrame>
    | undefined;
  private dataViewLastIntrinsicContentHeight = 0;
  private dataViewBodyMutationFrame: number | undefined;
  private dataViewBodyMutationObserver: MutationObserver | undefined;
  private dataViewBodyMutationTarget: HTMLElement | undefined;

  override disconnectedCallback(): void {
    this.disconnectDataViewBodyResizeObserver();
    super.disconnectedCallback();
  }

  private isDataViewBodyMeasurementPaused(): boolean {
    return this.closest('.block-idle') !== null;
  }

  protected syncDataViewBodyResizeObserver({
    body = getDataViewBodyElement(this),
    extraTargets = [],
    observeBodyChildren = true,
    onResize = () => this.queueDataViewBodyIntrinsicHeightChange(),
  }: {
    body?: HTMLElement | null;
    extraTargets?: readonly (HTMLElement | null | undefined)[];
    observeBodyChildren?: boolean;
    onResize?: () => void;
  } = {}): void {
    if (this.isDataViewBodyMeasurementPaused()) {
      return;
    }
    this.queueDataViewBodyContractValidation();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.syncDataViewBodyMutationObserver({
      body,
      extraTargets,
      observeBodyChildren,
      onResize,
    });
    const targets = collectDataViewBodyResizeTargets({
      body,
      extraTargets,
      fallback: this,
      observeBodyChildren,
    });
    if (
      targets.length === this.dataViewBodyResizeTargets.length &&
      targets.every(
        (target, index) => target === this.dataViewBodyResizeTargets[index]
      )
    ) {
      return;
    }

    this.dataViewBodyResizeObserver?.disconnect();
    this.dataViewBodyResizeTargets = targets;
    this.dataViewBodyResizeObserver = new ResizeObserver(() => {
      if (!this.isDataViewBodyMeasurementPaused()) {
        onResize();
      }
    });
    for (const target of targets) {
      this.dataViewBodyResizeObserver.observe(target);
    }
  }

  protected disconnectDataViewBodyResizeObserver(): void {
    this.dataViewBodyResizeObserver?.disconnect();
    this.dataViewBodyResizeObserver = undefined;
    this.dataViewBodyResizeTargets = [];
    this.dataViewBodyMutationObserver?.disconnect();
    this.dataViewBodyMutationObserver = undefined;
    this.dataViewBodyMutationTarget = undefined;
    if (this.dataViewBodyMutationFrame !== undefined) {
      cancelAnimationFrame(this.dataViewBodyMutationFrame);
      this.dataViewBodyMutationFrame = undefined;
    }
    if (this.dataViewBodyContractFrame !== undefined) {
      cancelAnimationFrame(this.dataViewBodyContractFrame);
      this.dataViewBodyContractFrame = undefined;
    }
    if (this.dataViewIntrinsicHeightChangeFrame !== undefined) {
      cancelAnimationFrame(this.dataViewIntrinsicHeightChangeFrame);
      this.dataViewIntrinsicHeightChangeFrame = undefined;
    }
  }

  private syncDataViewBodyMutationObserver({
    body,
    extraTargets,
    observeBodyChildren,
    onResize,
  }: {
    body?: HTMLElement | null;
    extraTargets: readonly (HTMLElement | null | undefined)[];
    observeBodyChildren: boolean;
    onResize: () => void;
  }): void {
    if (
      !body ||
      !observeBodyChildren ||
      typeof MutationObserver === 'undefined'
    ) {
      this.dataViewBodyMutationObserver?.disconnect();
      this.dataViewBodyMutationObserver = undefined;
      this.dataViewBodyMutationTarget = undefined;
      return;
    }
    if (this.dataViewBodyMutationTarget === body) {
      return;
    }

    this.dataViewBodyMutationObserver?.disconnect();
    this.dataViewBodyMutationTarget = body;
    this.dataViewBodyMutationObserver = new MutationObserver(() => {
      if (this.isDataViewBodyMeasurementPaused()) {
        return;
      }
      if (this.dataViewBodyMutationFrame !== undefined) {
        return;
      }
      this.dataViewBodyMutationFrame = requestAnimationFrame(() => {
        this.dataViewBodyMutationFrame = undefined;
        if (this.isDataViewBodyMeasurementPaused()) {
          return;
        }
        this.dataViewBodyResizeTargets = [];
        this.syncDataViewBodyResizeObserver({
          body,
          extraTargets,
          observeBodyChildren,
          onResize,
        });
        onResize();
      });
    });
    this.dataViewBodyMutationObserver.observe(body, {
      attributeFilter: [
        DATA_VIEW_BODY_CONTENT_ATTRIBUTE,
        DATA_VIEW_BODY_INTRINSIC_ATTRIBUTE,
        DATA_VIEW_BODY_RENDER_CONTENT_ATTRIBUTE,
        DATA_VIEW_BODY_RENDER_ROOT_ATTRIBUTE,
        DATA_VIEW_BODY_STATE_ATTRIBUTE,
      ],
      attributes: true,
      childList: true,
      subtree: true,
    });
  }

  getIntrinsicContentHeight(_visualScale = 1): number {
    return 0;
  }

  queueIntrinsicContentHeightChange(): void {
    if (this.isDataViewBodyMeasurementPaused()) {
      return;
    }
    if (this.dataViewIntrinsicHeightChangeFrame !== undefined) {
      return;
    }
    this.dataViewIntrinsicHeightChangeFrame = requestAnimationFrame(() => {
      this.dataViewIntrinsicHeightChangeFrame = undefined;
      if (this.isDataViewBodyMeasurementPaused()) {
        return;
      }
      const height = measureGfxPerformance(
        this.logic.root.config.performanceScope,
        'database-height-sync',
        () => this.getIntrinsicContentHeight(),
        () => this.logic.root.config.dataSource.rowCount$.value
      );
      if (Math.abs(height - this.dataViewLastIntrinsicContentHeight) <= 1) {
        return;
      }
      this.dataViewLastIntrinsicContentHeight = height;
      this.dispatchEvent(
        new CustomEvent(DATABASE_LIVE_VIEW_INTRINSIC_HEIGHT_CHANGE_EVENT, {
          bubbles: true,
          composed: true,
          detail: { height },
        })
      );
    });
  }

  private queueDataViewBodyIntrinsicHeightChange(): void {
    this.queueIntrinsicContentHeightChange();
  }

  private queueDataViewBodyContractValidation(): void {
    if (this.isDataViewBodyMeasurementPaused()) {
      return;
    }
    if (this.dataViewBodyContractFrame !== undefined) {
      return;
    }
    this.dataViewBodyContractFrame = requestAnimationFrame(() => {
      this.dataViewBodyContractFrame = undefined;
      this.validateDataViewBodyContract();
    });
  }

  private validateDataViewBodyContract(): void {
    if (this.isDataViewBodyMeasurementPaused()) {
      return;
    }
    measureGfxPerformance(
      this.logic.root.config.performanceScope,
      'database-body-contract',
      () => this.runDataViewBodyContractValidation(),
      () => 1
    );
  }

  private runDataViewBodyContractValidation(): void {
    const body = getDataViewBodyElement(this);
    if (!body) {
      this.reportDataViewBodyContractViolation(
        checkDataViewBodyContract({
          body,
          bodyState: this.logic.isSetupWizardOpen() ? 'setup' : 'data',
          visualScale: 1,
        })
      );
      return;
    }

    const bodyRect = body.getBoundingClientRect();
    if (bodyRect.width <= 0 || bodyRect.height <= 0) {
      return;
    }

    const check = checkDataViewBodyContract({
      body,
      host: this,
      visualScale: 1,
    });
    this.reportDataViewBodyContractViolation(check);
  }

  private reportDataViewBodyContractViolation(
    check: ReturnType<typeof checkDataViewBodyContract>
  ): void {
    if (!check.violation) {
      this.dataViewBodyContractViolationCandidateCount = 0;
      this.dataViewBodyContractViolationCandidateSignature = undefined;
      this.dataViewBodyContractViolationSignature = undefined;
      return;
    }

    const detail = {
      bodyState: check.bodyState,
      diagnostics: check.diagnostics,
      metrics: check.metrics,
      reason: check.violation.reason,
      viewId: this.logic.view.id,
      viewType: this.logic.view.type,
    };
    const signature = JSON.stringify({
      bodyState: detail.bodyState,
      reason: detail.reason,
      viewId: detail.viewId,
      viewType: detail.viewType,
    });
    if (signature === this.dataViewBodyContractViolationCandidateSignature) {
      this.dataViewBodyContractViolationCandidateCount += 1;
    } else {
      this.dataViewBodyContractViolationCandidateSignature = signature;
      this.dataViewBodyContractViolationCandidateCount = 1;
    }
    if (
      this.dataViewBodyContractViolationCandidateCount <
      DATA_VIEW_BODY_CONTRACT_STABLE_FRAME_THRESHOLD
    ) {
      this.queueDataViewBodyContractValidation();
      return;
    }
    if (signature === this.dataViewBodyContractViolationSignature) {
      return;
    }
    this.dataViewBodyContractViolationSignature = signature;

    console.error('[data-view-body] contract violated', detail);
    this.dispatchEvent(
      new CustomEvent('data-view-body-contract-violation', {
        bubbles: true,
        composed: true,
        detail,
      })
    );
  }
}

export abstract class DataViewUILogicBase<
  T extends SingleView = SingleView,
  Selection extends DataViewSelection = DataViewSelection,
> {
  constructor(
    public readonly root: DataViewRootUILogic,
    public readonly view: T
  ) {}

  get headerWidget(): DataViewWidget | undefined {
    return this.root.config.headerWidget;
  }
  bindHotkey(hotkeys: Record<string, UIEventHandler>): DisposableMember {
    return this.root.config.bindHotkey(
      Object.fromEntries(
        Object.entries(hotkeys).map(([key, fn]) => [
          key,
          ctx => {
            return fn(ctx);
          },
        ])
      )
    );
  }
  handleEvent(name: EventName, handler: UIEventHandler): DisposableMember {
    return this.root.config.handleEvent(name, context => {
      return handler(context);
    });
  }
  setSelection(selection?: Selection) {
    this.root.setSelection(selection);
  }

  isSetupWizardOpen(): boolean {
    return false;
  }

  selection$ = computed<Selection | undefined>(() => {
    const selection$ = this.root.selection$;
    if (selection$.value?.viewId === this.view.id) {
      return selection$.value as Selection | undefined;
    }
    return;
  });

  eventTrace: EventTraceFn<DatabaseAllViewEvents> = (key, params) => {
    this.root.config.eventTrace(key, {
      ...(params as DatabaseAllEvents[typeof key]),
      viewId: this.view.id,
      viewType: this.view.type,
    });
  };

  abstract clearSelection: () => void;
  abstract addRow: (position: InsertToPosition) => string | undefined;
  abstract focusFirstCell: () => void;
  abstract showIndicator: (evt: MouseEvent) => boolean;
  abstract hideIndicator: () => void;
  abstract moveTo: (id: string, evt: MouseEvent) => void;

  abstract renderer: UniComponent<{
    logic: DataViewUILogicBase<T, Selection>;
  }>;
}

type Constructor<T extends abstract new (...args: any) => any> = new (
  ...args: ConstructorParameters<T>
) => InstanceType<T>;

export type DataViewUILogicBaseConstructor = Constructor<
  typeof DataViewUILogicBase
>;
